import React, { useState } from 'react';
import { Button, Collapse, Tooltip } from 'antd';
import { Period, ViewRange } from './types';
import {
  intersects,
  kindColor,
  periodWindow,
  relatedPeriods,
} from './temporal';
import { periodDates, readable } from './format';

type Props = {
  periods: Period[];
  selected: Period | null;
  range: ViewRange;
  onSelect: (period: Period) => void;
  disabled?: boolean;
};

const IntervalTracks: React.FC<Props> = ({
  periods,
  selected,
  range,
  onSelect,
  disabled,
}) => {
  const [expanded, setExpanded] = useState(false);
  const visible = periods.filter(period => intersects(period, range));
  const scales = ['days', 'weeks', 'months', 'years', 'other'];
  const related = selected ? relatedPeriods(selected, periods) : [];
  const span = Math.max(1, range.max - range.min);
  return (
    <div className="market-explorer__interval-section">
      <div className="market-explorer__track-heading">
        <span>
          Periods in view <strong>{visible.length}</strong>
        </span>
        <Tooltip title="Bars show interval duration, clipped to the visible dates. Colors identify pattern types. Nested time scales remain separate.">
          <span tabIndex={0}>ⓘ</span>
        </Tooltip>
      </div>
      <div className="market-explorer__tracks">
        {scales.map(scale => {
          const matching = visible.filter(
            period => (period.horizon || 'other') === scale,
          );
          if (!matching.length) return null;
          const shown = matching.slice(0, expanded ? 10 : 3);
          if (
            selected &&
            matching.some(p => p.id === selected.id) &&
            !shown.some(p => p.id === selected.id)
          )
            shown.push(selected);
          return (
            <div className="market-explorer__track-scale" key={scale}>
              <span className="market-explorer__track-label">
                {readable(scale)} <small>{matching.length}</small>
              </span>
              <div className="market-explorer__track-lanes">
                {shown.map(period => {
                  const bounds = periodWindow(period);
                  const left = Math.max(
                    0,
                    ((bounds.min - range.min) / span) * 100,
                  );
                  const width = Math.max(
                    0.7,
                    ((Math.min(range.max, bounds.max) -
                      Math.max(range.min, bounds.min)) /
                      span) *
                      100,
                  );
                  return (
                    <div
                      className="market-explorer__track-lane"
                      key={period.id}
                    >
                      <Tooltip
                        title={`${period.title} · ${periodDates(period.start_date, period.end_date)}${bounds.min < range.min || bounds.max > range.max ? ' · continues outside view' : ''}`}
                      >
                        <button
                          type="button"
                          disabled={disabled}
                          aria-label={`${period.title}, ${periodDates(period.start_date, period.end_date)}`}
                          aria-pressed={period.id === selected?.id}
                          onClick={() => onSelect(period)}
                          className="market-explorer__interval-bar"
                          style={{
                            left: `${left}%`,
                            width: `${Math.min(100 - left, width)}%`,
                            backgroundColor: kindColor(period.kind),
                          }}
                        >
                          <span>{period.title}</span>
                        </button>
                      </Tooltip>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
      {visible.length > 6 && (
        <Button
          type="link"
          size="small"
          onClick={() => setExpanded(value => !value)}
        >
          {expanded ? 'Compact tracks' : 'More interval tracks'}
        </Button>
      )}
      {selected && related.length > 0 && (
        <Collapse
          ghost
          size="small"
          items={[
            {
              key: 'related',
              label: `Related views of this interval · ${related.length}`,
              children: (
                <div className="market-explorer__related-periods">
                  <p>
                    Similar windows are alternative views; shorter and longer
                    patterns remain separate.
                  </p>
                  {related.slice(0, 12).map(item => (
                    <button
                      type="button"
                      disabled={disabled}
                      key={item.period.id}
                      onClick={() => onSelect(item.period)}
                    >
                      <span
                        style={{ borderLeftColor: kindColor(item.period.kind) }}
                      >
                        <strong>{item.period.title}</strong>
                        <small>
                          {item.kind === 'similar'
                            ? 'Similar window'
                            : item.kind === 'contains'
                              ? 'Contains selected period'
                              : item.kind === 'inside'
                                ? 'Inside selected period'
                                : 'Partly overlaps'}{' '}
                          · {item.overlapDays} calendar days shared ·{' '}
                          {periodDates(
                            item.period.start_date,
                            item.period.end_date,
                          )}
                        </small>
                      </span>
                    </button>
                  ))}
                  {related.length > 12 && (
                    <small>
                      {related.length - 12} more overlaps remain available in
                      the period list.
                    </small>
                  )}
                </div>
              ),
            },
          ]}
        />
      )}
    </div>
  );
};

export default IntervalTracks;
